
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEOMetadata from '@/components/SEOMetadata';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEOMetadata
        title="Privacy Policy | NEXX TELECOM"
        description="NEXX TELECOM Privacy Policy outlining our data collection, usage, and protection practices for all services including VPS, proxies, and consulting."
        keywords="privacy policy, data protection, GDPR compliance, data security"
        canonical="https://nexx.net/privacy-policy"
        robots="noindex, nofollow"
      />
      <Header />
      
      <div className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <div className="mb-8">
            <Link to="/">
              <Button variant="ghost" className="text-nexx-green hover:text-green-700">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>

          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4 flex items-center justify-center">
              <span className="mr-3">🔒</span>
              NEXX TELECOM – Privacy Policy
            </h1>
            <p className="text-lg text-gray-600">
              Effective Date: June 4th, 2025
            </p>
            <p className="text-sm text-gray-500">
              Last Updated: June 4th, 2025
            </p>
            <div className="w-24 h-px bg-gray-300 mx-auto mt-6"></div>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 leading-relaxed mb-8">
              NEXX TELECOM LLC ("we," "us," or "our") is committed to protecting the privacy and security of our clients and visitors. This Privacy Policy explains how we collect, use, and protect your personal data when you interact with our website, services, and communication channels.
            </p>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Information We Collect */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">1. Information We Collect</h2>
              <p className="text-gray-700 mb-4">We collect the following types of information:</p>
              
              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">a. Personal Information</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Phone number (optional)</li>
                  <li>Company name</li>
                  <li>Billing details (if purchasing services)</li>
                </ul>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">b. Technical Data</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>IP addresses</li>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>Access times and referring URLs</li>
                  <li>Usage logs for internal diagnostics (for VPS/proxy/VPN activity only in aggregate, not user content)</li>
                </ul>
              </div>

              <div className="mb-6">
                <h3 className="text-xl font-semibold text-gray-800 mb-3">c. Service-Related Data</h3>
                <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                  <li>Service preferences</li>
                  <li>Configuration details (for custom deployments)</li>
                  <li>Support tickets or consultation notes</li>
                </ul>
              </div>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* How We Use Information */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">2. How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">We use your information to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                <li>Provision, manage, and maintain your services</li>
                <li>Respond to your inquiries or support requests</li>
                <li>Process billing and transactions</li>
                <li>Improve our service performance and offerings</li>
                <li>Detect, prevent, and respond to abuse or violations</li>
                <li>Comply with applicable laws and regulations</li>
              </ul>
              <p className="text-gray-700 font-semibold">
                We do not sell, rent, or trade your personal information to third parties.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Cookies & Analytics */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">3. Cookies & Analytics</h2>
              <p className="text-gray-700 mb-4">Our website uses cookies to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                <li>Understand visitor behavior (Google Analytics or similar)</li>
                <li>Maintain secure sessions</li>
                <li>Improve user experience</li>
              </ul>
              <p className="text-gray-700">
                You may adjust cookie preferences via your browser settings at any time.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Data Retention */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">4. Data Retention</h2>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Account data is retained for the duration of your active relationship with NEXX TELECOM.</li>
                <li>Billing data is retained as required by tax and financial regulations.</li>
                <li>Service logs are retained for operational security and compliance, but not indefinitely.</li>
              </ul>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Data Security */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">5. Data Security</h2>
              <p className="text-gray-700 mb-4">We employ physical, digital, and procedural safeguards to protect your information, including:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
                <li>Encrypted storage of sensitive data</li>
                <li>Two-factor authentication (2FA) for administrative access</li>
                <li>Internal access restrictions on all personal data</li>
                <li>Regular audits and access monitoring</li>
              </ul>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Third-Party Processors */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">6. Third-Party Processors</h2>
              <p className="text-gray-700">
                We may use third-party services (e.g., Stripe, Google, DigitalOcean, or others) to help us operate. These providers are contractually obligated to follow strict data handling practices and are GDPR-compliant where applicable.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Your Rights */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">7. Your Rights</h2>
              <p className="text-gray-700 mb-4">Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4 mb-4">
                <li>Access the personal data we hold about you</li>
                <li>Request correction or deletion</li>
                <li>Object to certain processing activities</li>
                <li>Withdraw consent where applicable</li>
              </ul>
              <p className="text-gray-700">
                To exercise any of these rights, contact us at{' '}
                <a href="mailto:privacy@nexx.net" className="text-nexx-green hover:underline">privacy@nexx.net</a>.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Law Enforcement */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">8. Law Enforcement & Compliance</h2>
              <p className="text-gray-700">
                We will only disclose data to law enforcement upon receiving a valid legal request such as a subpoena, court order, or equivalent. We do not comply with informal or extra-jurisdictional requests without due process.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Updates */}
            <div className="mb-12">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">9. Updates to This Policy</h2>
              <p className="text-gray-700">
                This Privacy Policy may be updated periodically. Material changes will be announced via email or on our website. Continued use of our services after updates constitutes your agreement to the revised policy.
              </p>
            </div>

            <div className="w-full h-px bg-gray-200 my-8"></div>

            {/* Contact */}
            <div className="mb-12 text-center">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6">10. Contact Us</h2>
              <p className="text-gray-700 mb-4">For privacy-related inquiries or data access requests:</p>
              <div className="space-y-2 text-gray-700">
                <p>📧 <a href="mailto:support@nexx.net" className="text-nexx-green hover:underline">support@nexx.net</a></p>
                <p>📍 NEXX TELECOM LLC – Beltsville, Maryland</p>
              </div>
              <div className="mt-6">
                <Button className="bg-nexx-green hover:bg-green-700 text-white">
                  <a href="mailto:support@nexx.net">Contact Support</a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
